import React, { useState, useEffect } from 'react';
import { TimesheetModel, Struttura } from '../../models/models';
import { useUser } from '../../context/usercontext';
import { fetchStrutture } from '../../services/struttureApi';
import { 
  fetchRegistrazioni, 
  addRegistrazione, 
  updateRegistrazione, 
  deleteRegistrazione 
} from './timesheetApi';
import { 
  ordinaRegistrazioni, 
  calcolaOre, 
  calcolaTotaleOre, 
  validaOrari, 
  formattaDataItaliana,
  calcolaDataUscita
} from './timesheetUtils';

interface TimesheetPageProps {
  onLogout: () => void;
  userName: string;
}

const TimesheetPage: React.FC<TimesheetPageProps> = ({ onLogout, userName }) => {
  const [registrazioni, setRegistrazioni] = useState<TimesheetModel[]>([]);
  const [strutture, setStrutture] = useState<Struttura[]>([]);
  const [loadingStrutture, setLoadingStrutture] = useState(true);
  const [ufficio, setUfficio] = useState('');
  const [data, setData] = useState(new Date().toISOString().split('T')[0]);
  const [oraInizio, setOraInizio] = useState('');
  const [oraFine, setOraFine] = useState('');
  const [note, setNote] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [tooltipId, setTooltipId] = useState<number | null>(null);
  const [registrazioneOriginale, setRegistrazioneOriginale] = useState<TimesheetModel | null>(null);
  const [errorModal, setErrorModal] = useState<string | null>(null);
  const [confermaNotteModal, setConfermaNotteModal] = useState<boolean>(false);
  const [registrazionePendente, setRegistrazionePendente] = useState<TimesheetModel | null>(null);
  const [modificaPendente, setModificaPendente] = useState<{ id: number; registrazione: TimesheetModel } | null>(null);
  const [searchStruttura, setSearchStruttura] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchStrutturaEdit, setSearchStrutturaEdit] = useState('');
  const [isDropdownEditOpen, setIsDropdownEditOpen] = useState<number | null>(null);
  const [isExternal, setIsExternal] = useState(false);
  const [idSocieta, setIdSocieta] = useState(0);
  const [showOrariRapidiInizio, setShowOrariRapidiInizio] = useState(false);
  const [showOrariRapidiFine, setShowOrariRapidiFine] = useState(false);
  const [meseVisualizzato, setMeseVisualizzato] = useState(new Date().getMonth());
  const [annoVisualizzato, setAnnoVisualizzato] = useState(new Date().getFullYear());
  const { user } = useUser();

  const orariComuni = [
    '00:00', '00:15', '00:30', '00:45',
    '01:00', '01:15', '01:30', '01:45',
    '02:00', '02:15', '02:30', '02:45',
    '03:00', '03:15', '03:30', '03:45',
    '04:00', '04:15', '04:30', '04:45',
    '05:00', '05:15', '05:30', '05:45',
    '06:00', '06:15', '06:30', '06:45',
    '07:00', '07:15', '07:30', '07:45',
    '08:00', '08:15', '08:30', '08:45',
    '09:00', '09:15', '09:30', '09:45',
    '10:00', '10:15', '10:30', '10:45',
    '11:00', '11:15', '11:30', '11:45',
    '12:00', '12:15', '12:30', '12:45',
    '13:00', '13:15', '13:30', '13:45',
    '14:00', '14:15', '14:30', '14:45',
    '15:00', '15:15', '15:30', '15:45',
    '16:00', '16:15', '16:30', '16:45',
    '17:00', '17:15', '17:30', '17:45',
    '18:00', '18:15', '18:30', '18:45',
    '19:00', '19:15', '19:30', '19:45',
    '20:00', '20:15', '20:30', '20:45',
    '21:00', '21:15', '21:30', '21:45',
    '22:00', '22:15', '22:30', '22:45',
    '23:00', '23:15', '23:30', '23:45'
  ];

  // Carica le strutture dall'API
  useEffect(() => {
    const loadStrutture = async () => {
      try {
        const data = await fetchStrutture();
        setStrutture(data);
      } catch (error) {
        console.error('Errore nel caricamento delle strutture:', error);
      } finally {
        setLoadingStrutture(false);
      }
    };
    loadStrutture();
  }, []);

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
        setIsDropdownEditOpen(null);
      }
    };

    if (isDropdownOpen || isDropdownEditOpen !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isDropdownEditOpen]);

  // Carica le registrazioni dall'API
  useEffect(() => {
    const loadRegistrazioni = async () => {
      if (!user?.id) return;
      
      try {
        // Calcola primo e ultimo giorno del mese visualizzato
        const ultimoGiornoDelMese = new Date(annoVisualizzato, meseVisualizzato + 1, 0).getDate();
        
        // Formatta le date direttamente senza conversione UTC
        const dataPrimoGiorno = `${annoVisualizzato}-${String(meseVisualizzato + 1).padStart(2, '0')}-01`;
        const dataUltimoGiorno = `${annoVisualizzato}-${String(meseVisualizzato + 1).padStart(2, '0')}-${String(ultimoGiornoDelMese).padStart(2, '0')}`;
        const dataIngresso = `${dataPrimoGiorno}T00:00:00`;
        const dataUscita = `${dataUltimoGiorno}T23:59:00`;
        
        const data = await fetchRegistrazioni(user.id, dataIngresso, dataUscita);
        const registrazioniOrdinate = ordinaRegistrazioni(data);
        setRegistrazioni(registrazioniOrdinate);
      } catch (error) {
        console.error('Errore nel caricamento delle registrazioni:', error);
      }
    };
    loadRegistrazioni();
  }, [user, meseVisualizzato, annoVisualizzato]);

  const campiCompilati = ufficio && data && oraInizio && oraFine;

  // Funzioni per navigare tra i mesi
  const mesePrecedente = () => {
    if (meseVisualizzato === 0) {
      setMeseVisualizzato(11);
      setAnnoVisualizzato(annoVisualizzato - 1);
    } else {
      setMeseVisualizzato(meseVisualizzato - 1);
    }
  };

  const meseSuccessivo = () => {
    if (meseVisualizzato === 11) {
      setMeseVisualizzato(0);
      setAnnoVisualizzato(annoVisualizzato + 1);
    } else {
      setMeseVisualizzato(meseVisualizzato + 1);
    }
  };

  // Verifica se siamo nel mese corrente
  const oggi = new Date();
  const isMeseCorrente = meseVisualizzato === oggi.getMonth() && annoVisualizzato === oggi.getFullYear();

  // Nomi dei mesi
  const nomiMesi = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const aggiungiRegistrazione = async () => {
    if (!ufficio || !data || !oraInizio || !oraFine) {
      alert('Compila tutti i campi');
      return;
    }

    const dataUscita = calcolaDataUscita(data, oraInizio, oraFine);
    const nuovaRegistrazione: TimesheetModel = {
      id: 0,
      id_struttura: Number.parseInt(ufficio),
      id_operatore: user?.id || 0,
      data: data,
      oraInizio: oraInizio,
      oraFine: oraFine,
      data_ingresso: `${data}T${oraInizio}:00`,
      data_uscita: `${dataUscita}T${oraFine}:00`,
      note: note || null,
      external: isExternal,
      id_societa: idSocieta,
    };

    // Verifica se è un turno notturno
    if (!validaOrari(oraInizio, oraFine)) {
      setRegistrazionePendente(nuovaRegistrazione);
      setConfermaNotteModal(true);
      return;
    }

    await salvaNuovaRegistrazione(nuovaRegistrazione);
  };

  const salvaNuovaRegistrazione = async (nuovaRegistrazione: TimesheetModel) => {
    setRegistrazioni(prev => [...prev, nuovaRegistrazione]);
    
    try {
      const registrazioneTrasformata = await addRegistrazione(nuovaRegistrazione);
      setRegistrazioni(prev => {
        const updated = prev.map(r => r === nuovaRegistrazione ? registrazioneTrasformata : r);
        return ordinaRegistrazioni(updated);
      });
    } catch (error) {
      setErrorModal(error instanceof Error ? error.message : 'Errore di connessione');
      setRegistrazioni(prev => prev.filter(r => r !== nuovaRegistrazione));
      return;
    }
    
    // Reset campi
    setUfficio('');
    setData(new Date().toISOString().split('T')[0]);
    setOraInizio('');
    setOraFine('');
    setNote('');
    setSearchStruttura('');
    setIsExternal(false);
    setIdSocieta(0);
  };

  const confermaTurnoNotte = async () => {
    setConfermaNotteModal(false);
    if (registrazionePendente) {
      await salvaNuovaRegistrazione(registrazionePendente);
      setRegistrazionePendente(null);
    } else if (modificaPendente) {
      await salvaModificaConferma(modificaPendente.id, modificaPendente.registrazione);
      setModificaPendente(null);
    }
  };

  const annullaTurnoNotte = () => {
    setConfermaNotteModal(false);
    setRegistrazionePendente(null);
    setModificaPendente(null);
  };

  const avviaModifica = (id: number) => {
    const registrazione = registrazioni.find(r => r.id === id);
    if (registrazione) {
      setRegistrazioneOriginale({ ...registrazione });
    }
    setEditingId(id);
  };

  const salvaModifica = async (id: number) => {
    const registrazione = registrazioni.find(r => r.id === id);
    if (!registrazione) return;
    
    // Verifica se è un turno notturno
    if (!validaOrari(registrazione.oraInizio, registrazione.oraFine)) {
      // Ricalcola data_uscita prima di aprire il modale
      const dataUscita = calcolaDataUscita(registrazione.data, registrazione.oraInizio, registrazione.oraFine);
      const registrazioneConDataAggiornata = {
        ...registrazione,
        data_uscita: `${dataUscita}T${registrazione.oraFine}:00`
      };
      setModificaPendente({ id, registrazione: registrazioneConDataAggiornata });
      setConfermaNotteModal(true);
      return;
    }
    
    await salvaModificaConferma(id, registrazione);
  };

  const salvaModificaConferma = async (id: number, registrazione: TimesheetModel) => {
    // Ricalcola data_uscita per turni notturni
    const dataUscita = calcolaDataUscita(registrazione.data, registrazione.oraInizio, registrazione.oraFine);
    const registrazioneAggiornata = {
      ...registrazione,
      data_ingresso: `${registrazione.data}T${registrazione.oraInizio}:00`,
      data_uscita: `${dataUscita}T${registrazione.oraFine}:00`
    };
    
    try {
      const registrazioneTrasformata = await updateRegistrazione(id, registrazioneAggiornata);
      setRegistrazioni(prev => prev.map(r => 
        r.id === id ? registrazioneTrasformata : r
      ));
    } catch (error) {
      setErrorModal(error instanceof Error ? error.message : 'Errore di connessione');
      if (registrazioneOriginale) {
        setRegistrazioni(prev => prev.map(r => 
          r.id === id ? registrazioneOriginale : r
        ));
      }
      return;
    }
    
    setEditingId(null);
    setRegistrazioneOriginale(null);
  };

  const annullaModifica = () => {
    if (editingId !== null && registrazioneOriginale) {
      setRegistrazioni(registrazioni.map(r => 
        r.id === editingId ? registrazioneOriginale : r
      ));
    }
    setEditingId(null);
    setRegistrazioneOriginale(null);
    setIsDropdownEditOpen(null);
    setSearchStrutturaEdit('');
  };

  const aggiornaRegistrazione = (id: number, campo: string, valore: string) => {
    setRegistrazioni(registrazioni.map(r => {
      if (r.id === id) {
        if (campo === 'external') {
          return { ...r, [campo]: valore === 'true' };
        }
        if (campo === 'id_societa') {
          return { ...r, [campo]: Number.parseInt(valore) };
        }
        return { ...r, [campo]: valore };
      }
      return r;
    }));
  };

  const rimuoviRegistrazione = async (id: number) => {
    const registrazioneRimossa = registrazioni.find(r => r.id === id);
    setRegistrazioni(registrazioni.filter(r => r.id !== id));
    if (editingId === id) {
      setEditingId(null);
    }

    try {
      await deleteRegistrazione(id);
    } catch (error) {
      setErrorModal(error instanceof Error ? error.message : 'Errore di connessione');
      if (registrazioneRimossa) {
        setRegistrazioni(prev => [...prev, registrazioneRimossa]);
      }
    }
  };

  const toggleTooltip = (id: number) => {
    setTooltipId(tooltipId === id ? null : id);
  };

  const strutturaSelezionata = strutture.find(s => s.id.toString() === ufficio);
  const struttureFiltrate = strutture.filter(s => 
    s.nome_struttura.toLowerCase().includes(searchStruttura.toLowerCase()) ||
    s.indirizzo?.toLowerCase().includes(searchStruttura.toLowerCase())
  );

  const selezionaStruttura = (id: string) => {
    setUfficio(id);
    setIsDropdownOpen(false);
    setSearchStruttura('');
  };

  const struttureFiltateEdit = strutture.filter(s => 
    s.nome_struttura.toLowerCase().includes(searchStrutturaEdit.toLowerCase()) ||
    s.indirizzo?.toLowerCase().includes(searchStrutturaEdit.toLowerCase())
  );

  const selezionaStrutturaEdit = (registrazioneId: number, strutturaId: string) => {
    aggiornaRegistrazione(registrazioneId, 'id_struttura', strutturaId);
    setIsDropdownEditOpen(null);
    setSearchStrutturaEdit('');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-5">
      {/* Modale Errore */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6 text-white" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <h3 className="text-2xl font-bold">⚠️ Errore</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-6">{errorModal}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => setErrorModal(null)}
                  className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all font-medium"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale Conferma Turno Notturno */}
      {confermaNotteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <h3 className="text-2xl font-bold">🌙 Turno Notturno</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                L'orario di fine è precedente all'orario di inizio.
              </p>
              <p className="text-gray-900 font-semibold mb-6">
                Hai effettuato un turno notturno con proseguimento nel giorno successivo?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={annullaTurnoNotte}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all font-medium"
                >
                  No, correggi
                </button>
                <button
                  onClick={confermaTurnoNotte}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium"
                >
                  Sì, conferma
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-8 text-white flex justify-between items-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div>
              <h1 className="text-3xl font-bold mb-2">⏰ Timesheet Online</h1>
              <p className="opacity-90">Registra le tue ore di lavoro</p>
            </div>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all"
            >
              🚪 Esci
            </button>
          </div>

          <div className="p-8">
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">
                📋 Nuova Registrazione
              </h2>
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-purple-500">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="relative dropdown-container">
                    <label className="block mb-2 font-medium text-gray-600">🏢 Struttura</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => !loadingStrutture && setIsDropdownOpen(!isDropdownOpen)}
                        disabled={loadingStrutture}
                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none text-left flex items-center justify-between disabled:bg-gray-100 disabled:cursor-not-allowed bg-white"
                      >
                        <span className={strutturaSelezionata ? 'text-gray-900' : 'text-gray-400'}>
                          {loadingStrutture 
                            ? 'Caricamento...' 
                            : strutturaSelezionata 
                              ? `${strutturaSelezionata.nome_struttura} - ${strutturaSelezionata.indirizzo ? strutturaSelezionata.indirizzo : ''}`
                              : 'Seleziona Struttura...'
                          }
                        </span>
                        <span className="text-gray-400">▼</span>
                      </button>
                      
                      {isDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg">
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              placeholder="🔍 Cerca struttura..."
                              value={searchStruttura}
                              onChange={(e) => setSearchStruttura(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded focus:border-purple-500 focus:outline-none"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {struttureFiltrate.length > 0 ? (
                              struttureFiltrate.map(s => (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => selezionaStruttura(s.id.toString())}
                                  className={`w-full text-left p-3 hover:bg-purple-50 transition-colors border-b last:border-b-0 ${
                                    s.id.toString() === ufficio ? 'bg-purple-100' : ''
                                  }`}
                                >
                                  <div className="font-medium">{s.nome_struttura}</div>
                                  <div className="text-sm text-gray-500">{s.indirizzo}</div>
                                </button>
                              ))
                            ) : (
                              <div className="p-4 text-center text-gray-400">
                                Nessuna struttura trovata
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">📅 Data</label>
                    <input
                      type="date"
                      value={data}
                      onChange={(e) => setData(e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-4 md:items-end">
                  <div className="flex-1">
                    <label className="block mb-2 font-medium text-gray-600">🕐 Orari di Lavoro</label>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 relative">
                        <input
                          type="time"
                          value={oraInizio}
                          onChange={(e) => setOraInizio(e.target.value)}
                          onFocus={() => window.innerWidth >= 768 && setShowOrariRapidiInizio(true)}
                          onBlur={() => setTimeout(() => setShowOrariRapidiInizio(false), 200)}
                          className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        />
                        {showOrariRapidiInizio && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-purple-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                            <div className="sticky top-0 bg-purple-50 px-3 py-2 border-b flex justify-between items-center">
                              <span className="text-sm font-semibold text-purple-700">⚡ Ora ingresso</span>
                              <button
                                onClick={() => setShowOrariRapidiInizio(false)}
                                className="text-purple-600 hover:text-purple-800 font-bold"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="grid grid-cols-4 gap-1 p-2">
                              {orariComuni.map(ora => (
                                <button
                                  key={ora}
                                  onClick={() => {
                                    setOraInizio(ora);
                                    setShowOrariRapidiInizio(false);
                                  }}
                                  className="p-2 text-sm bg-purple-50 hover:bg-purple-200 rounded transition-colors font-medium"
                                >
                                  {ora}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <span className="text-purple-600 font-bold text-xl">—</span>
                      <div className="flex-1 relative">
                        <input
                          type="time"
                          value={oraFine}
                          onChange={(e) => setOraFine(e.target.value)}
                          onFocus={() => window.innerWidth >= 768 && setShowOrariRapidiFine(true)}
                          onBlur={() => setTimeout(() => setShowOrariRapidiFine(false), 200)}
                          className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                        />
                        {showOrariRapidiFine && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-purple-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                            <div className="sticky top-0 bg-purple-50 px-3 py-2 border-b flex justify-between items-center">
                              <span className="text-sm font-semibold text-purple-700">⚡ Ora uscita</span>
                              <button
                                onClick={() => setShowOrariRapidiFine(false)}
                                className="text-purple-600 hover:text-purple-800 font-bold"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="grid grid-cols-4 gap-1 p-2">
                              {orariComuni.map(ora => (
                                <button
                                  key={ora}
                                  onClick={() => {
                                    setOraFine(ora);
                                    setShowOrariRapidiFine(false);
                                  }}
                                  className="p-2 text-sm bg-purple-50 hover:bg-purple-200 rounded transition-colors font-medium"
                                >
                                  {ora}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={aggiungiRegistrazione}
                    disabled={!campiCompilati}
                    className={`w-full md:w-auto px-6 py-3 rounded-lg text-white font-medium transition-all ${
                      campiCompilati 
                        ? 'bg-green-500 hover:bg-green-600 cursor-pointer' 
                        : 'bg-gray-400 cursor-not-allowed opacity-60'
                    }`}
                  >
                    + Aggiungi
                  </button>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">📍 Tipo di Servizio</label>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="tipoServizio"
                          checked={!isExternal}
                          onChange={() => setIsExternal(false)}
                          className="mr-2 w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-gray-700">🏢 Interno</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="tipoServizio"
                          checked={isExternal}
                          onChange={() => setIsExternal(true)}
                          className="mr-2 w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-gray-700">🌍 Esterno</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-gray-600">🏢 Società</label>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="societa"
                          checked={idSocieta === 0}
                          onChange={() => setIdSocieta(0)}
                          className="mr-2 w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-gray-700">Service</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="societa"
                          checked={idSocieta === 1}
                          onChange={() => setIdSocieta(1)}
                          className="mr-2 w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-gray-700">Atlanta</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block mb-2 font-medium text-gray-600">📝 Note (opzionale)</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Aggiungi eventuali note..."
                    rows={2}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">📊 Registrazioni</h2>
              {registrazioni.length === 0 ? (
                <p className="text-center text-gray-400 py-8">Nessuna registrazione ancora inserita</p>
              ) : (
                registrazioni.map(r => (
                  <div key={r.id} className={`bg-gray-50 p-5 rounded-lg border-l-4 mb-3 transition-all ${
                    editingId === r.id ? 'border-blue-400 bg-blue-50' : 'border-purple-500'
                  }`}>
                    {editingId === r.id ? (
                      // Modalità modifica inline
                      <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="relative dropdown-container">
                            <label className="block mb-1 text-sm font-medium text-gray-600">🏢 Struttura</label>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => setIsDropdownEditOpen(isDropdownEditOpen === r.id ? null : r.id)}
                                className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none text-left flex items-center justify-between bg-white"
                              >
                                <span className="text-gray-900">
                                  {strutture.find(s => s.id === r.id_struttura)?.nome_struttura || 'Seleziona Struttura...'} - ({strutture.find(s => s.id === r.id_struttura)?.indirizzo ? strutture.find(s => s.id === r.id_struttura)?.indirizzo : ''})
                                </span>
                                <span className="text-gray-400">▼</span>
                              </button>
                              
                              {isDropdownEditOpen === r.id && (
                                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-lg">
                                  <div className="p-2 border-b">
                                    <input
                                      type="text"
                                      placeholder="🔍 Cerca struttura..."
                                      value={searchStrutturaEdit}
                                      onChange={(e) => setSearchStrutturaEdit(e.target.value)}
                                      className="w-full p-2 border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                      autoFocus
                                    />
                                  </div>
                                  <div className="max-h-60 overflow-y-auto">
                                    {struttureFiltateEdit.length > 0 ? (
                                      struttureFiltateEdit.map(s => (
                                        <button
                                          key={s.id}
                                          type="button"
                                          onClick={() => selezionaStrutturaEdit(r.id, s.id.toString())}
                                          className={`w-full text-left p-3 hover:bg-blue-50 transition-colors border-b last:border-b-0 ${
                                            s.id === r.id_struttura ? 'bg-blue-100' : ''
                                          }`}
                                        >
                                          <div className="font-medium">{s.nome_struttura}</div>
                                          <div className="text-sm text-gray-500">{s.indirizzo}</div>
                                        </button>
                                      ))
                                    ) : (
                                      <div className="p-4 text-center text-gray-400">
                                        Nessuna struttura trovata
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-600">📅 Data</label>
                            <input
                              type="date"
                              value={r.data}
                              onChange={(e) => aggiornaRegistrazione(r.id, 'data', e.target.value)}
                              className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <label className="block mb-1 text-sm font-medium text-gray-600">🕐 Ora Inizio</label>
                            <input
                              type="time"
                              value={r.oraInizio}
                              onChange={(e) => aggiornaRegistrazione(r.id, 'oraInizio', e.target.value)}
                              className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block mb-1 text-sm font-medium text-gray-600">🕐 Ora Fine</label>
                            <input
                              type="time"
                              value={r.oraFine}
                              onChange={(e) => aggiornaRegistrazione(r.id, 'oraFine', e.target.value)}
                              className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none"
                            />
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-600">📍 Tipo di Servizio</label>
                            <div className="flex gap-4">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name={`tipoServizio-${r.id}`}
                                  checked={!r.external}
                                  onChange={() => aggiornaRegistrazione(r.id, 'external', 'false')}
                                  className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-gray-700">🏢 Interno</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name={`tipoServizio-${r.id}`}
                                  checked={r.external === true}
                                  onChange={() => aggiornaRegistrazione(r.id, 'external', 'true')}
                                  className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-gray-700">🌍 Esterno</span>
                              </label>
                            </div>
                          </div>
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-600">🏢 Società</label>
                            <div className="flex gap-4">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name={`societa-${r.id}`}
                                  checked={r.id_societa === 0}
                                  onChange={() => aggiornaRegistrazione(r.id, 'id_societa', '0')}
                                  className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-gray-700">Service</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name={`societa-${r.id}`}
                                  checked={r.id_societa === 1}
                                  onChange={() => aggiornaRegistrazione(r.id, 'id_societa', '1')}
                                  className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-gray-700">Atlanta</span>
                              </label>
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-600">📝 Note</label>
                          <textarea
                            value={r.note || ''}
                            onChange={(e) => aggiornaRegistrazione(r.id, 'note', e.target.value)}
                            placeholder="Aggiungi eventuali note..."
                            rows={2}
                            className="w-full p-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => annullaModifica()}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all text-sm"
                          >
                            ✕ Annulla
                          </button>
                          <button
                            onClick={() => salvaModifica(r.id)}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all text-sm"
                          >
                            ✓ Salva
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Modalità visualizzazione
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex-1 min-w-[150px]">
                          <strong>🏢 { strutture.find(s => s.id === r.id_struttura)?.nome_struttura }</strong>
                          <div className="mt-1">
                            <span className={`text-xs px-2 py-1 rounded ${r.id_societa === 0 ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                              {r.id_societa === 0 ? 'Service' : 'Atlanta'}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-[120px]">
                          <strong>📅 {formattaDataItaliana(r.data)}</strong>
                        </div>
                        <div className="flex-1 min-w-[150px]">
                          <strong>🕐 {r.oraInizio} — {r.oraFine}</strong>
                          {(() => {
                            const [oreInizio, minInizio] = r.oraInizio.split(':').map(Number);
                            const [oreFine, minFine] = r.oraFine.split(':').map(Number);
                            const minutiInizio = oreInizio * 60 + minInizio;
                            const minutiFine = oreFine * 60 + minFine;
                            if (minutiFine < minutiInizio) {
                              return <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">🌙 +1 giorno</span>;
                            }
                            return null;
                          })()}
                        </div>
                        <div className="text-green-600 font-bold">
                          ⏱️ {calcolaOre(r.oraInizio, r.oraFine)}
                        </div>
                        {r.note && (
                          <div className="relative">
                            <button
                              onClick={() => toggleTooltip(r.id)}
                              onMouseEnter={() => setTooltipId(r.id)}
                              onMouseLeave={() => setTooltipId(null)}
                              className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-all text-sm border border-yellow-300"
                              title="Clicca o passa il mouse per vedere la nota"
                            >
                              📝
                            </button>
                            {tooltipId === r.id && (
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg z-10">
                                <div className="font-semibold mb-1">📝 Nota:</div>
                                <div className="whitespace-pre-wrap">{r.note}</div>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                  <div className="border-8 border-transparent border-t-gray-800"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={() => avviaModifica(r.id)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all text-sm"
                            title="Modifica"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => rimuoviRegistrazione(r.id)}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-all text-sm"
                            title="Elimina"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Navigazione Mesi */}
            <div className="mb-6 flex flex-col min-[480px]:flex-row items-stretch min-[480px]:items-center justify-center gap-3 sm:gap-4">
              <div className="order-1 min-[480px]:order-2 text-center px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg border-2 border-purple-300 w-full min-[480px]:w-auto min-[480px]:min-w-[220px]">
                <div className="text-lg sm:text-xl font-bold text-purple-700">
                  {nomiMesi[meseVisualizzato]} {annoVisualizzato}
                </div>
                {isMeseCorrente && (
                  <div className="text-xs text-purple-600 mt-1">📅 Mese corrente</div>
                )}
              </div>

              <button
                onClick={mesePrecedente}
                className="order-2 min-[480px]:order-1 w-full min-[480px]:w-auto px-4 sm:px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all font-medium shadow-md hover:shadow-lg text-sm sm:text-base"
                title="Mese precedente"
              >
                ← Precedente
              </button>
              <button
                onClick={meseSuccessivo}
                disabled={isMeseCorrente}
                className={`order-3 w-full min-[480px]:w-auto px-4 sm:px-6 py-3 rounded-lg font-medium shadow-md transition-all text-sm sm:text-base ${
                  isMeseCorrente
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-500 text-white hover:bg-purple-600 hover:shadow-lg'
                }`}
                title={isMeseCorrente ? 'Non puoi andare nel futuro' : 'Mese successivo'}
              >
                Successivo →
              </button>
            </div>

            <div className="bg-blue-50 p-5 rounded-lg border-2 border-blue-400">
              <div className="text-center text-2xl font-bold text-blue-600">
                Totale ore: {calcolaTotaleOre(registrazioni)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimesheetPage;
